import { deserialize, fetchWrapper } from '../fetch';
import type {
  TagCount,
  Task,
  TaskComment,
  TaskLink,
  TaskPriority,
  TaskStatus,
} from './interface';

export const fetchDossierTasks = async (dossierId: string): Promise<Task[]> => {
  const url = new URL(`/app/v1/dossiers/${dossierId}/tasks`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

// Distinct task tags across the whole workspace (with usage counts), for the
// tag-editor autocomplete. Dossier-scoped tag lists are derived client-side.
export const fetchAllTaskTags = async (): Promise<{ tags: TagCount[] }> => {
  const url = new URL(`/app/v1/task-tags`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

// Atomic single-tag add/remove. The server merges the change, so the client
// never sends a full tags array and can't clobber tags it didn't author.
export const addTaskTag = async (
  taskId: string,
  tag: string
): Promise<Task> => {
  const url = new URL(`/app/v1/tasks/${taskId}/tags`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({ tag }),
    })
  );
};

export const removeTaskTag = async (
  taskId: string,
  tag: string
): Promise<Task> => {
  const url = new URL(
    `/app/v1/tasks/${taskId}/tags/${encodeURIComponent(tag)}`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'DELETE', credentials: 'include' })
  );
};

export const fetchTask = async (taskId: string): Promise<Task> => {
  const url = new URL(`/app/v1/tasks/${taskId}`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchTaskNotes = async (
  taskId: string
): Promise<{ notes: TaskComment[] }> => {
  const url = new URL(`/app/v1/tasks/${taskId}/notes`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const createTask = async (data: {
  dossierId: string;
  title: string;
  due?: string | null;
  priority?: TaskPriority;
  assignee?: string | null;
  link?: TaskLink | null;
  source?: string;
  description?: string | null;
  tags?: string[];
}): Promise<Task> => {
  const { dossierId, ...body } = data;
  const url = new URL(`/app/v1/dossiers/${dossierId}/tasks`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  );
};

export const updateTask = async (data: {
  dossierId: string;
  taskId: string;
  title?: string;
  due?: string | null;
  priority?: TaskPriority;
  assignee?: string | null;
  status?: TaskStatus;
  link?: TaskLink | null;
  description?: string | null;
  tags?: string[];
}): Promise<Task> => {
  const { dossierId: _dossierId, taskId, ...body } = data;
  void _dossierId;
  const url = new URL(`/app/v1/tasks/${taskId}`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify(body),
    })
  );
};

export const respondToTask = async ({
  taskId,
  ...data
}: {
  taskId: string;
  intent: 'resolve' | 'defer';
  description: string;
  defer_to?: string;
}): Promise<{ file_id: string }> => {
  const url = new URL(`/app/v1/tasks/${taskId}/respond`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify(data),
    })
  );
};

export const commentOnTask = async ({
  taskId,
  ...data
}: {
  taskId: string;
  text: string;
  mentions?: string[];
  scope?: 'comment' | 'claim';
}): Promise<{ file_id: string }> => {
  const url = new URL(`/app/v1/tasks/${taskId}/comment`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify(data),
    })
  );
};

export const deleteTask = async (data: {
  dossierId: string;
  taskId: string;
}): Promise<void> => {
  const url = new URL(`/app/v1/tasks/${data.taskId}`, API_BASE_URL);
  return deserialize(
    await fetchWrapper(url.href, {
      method: 'DELETE',
      credentials: 'include',
    })
  );
};
